// frontend/src/Components/Tasks/TaskDetailModal.jsx
//
// Full task body, action buttons, comment thread. Opens over the dashboard
// (or any page) when the user clicks a task row OR when a notification deep-
// links to ?taskId=… and the home page consumes the param.
//
// Loads task + comments on open, fetches practice users once for the
// @mention picker, and re-fetches the task after each comment submit so the
// status badge / activity timestamp / unread state stay in sync without a
// full refresh.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Dropdown, Form, Modal, OverlayTrigger, Popover, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import {
  getTask,
  addTaskComment,
  patchTaskStatus,
  patchTaskAssignee,
  markTaskRead,
  listPracticeUsers,
  uploadTaskAttachments,
} from '../../api';
import { approveTask, rejectTask } from '../../afinanceapi';
import TaskCommentList from './TaskCommentList';
import { useRecordingActions } from '../SyncTo/RecordingProvider';

// Mirror CreateTaskModal so the cap and accepted formats are identical
// regardless of which surface the user uses to add images.
const MAX_ATTACHMENTS = 5;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

function statusVariant(status) {
  if (status === 'NEW') return 'primary';
  if (status === 'IN_PROGRESS') return 'warning';
  if (status === 'DONE') return 'success';
  return 'secondary';
}
function statusLabel(status) {
  if (status === 'IN_PROGRESS') return 'In Progress';
  if (status === 'NEW') return 'To Do';
  if (status === 'DONE') return 'Done';
  return status;
}

function priorityBadgeProps(p) {
  const v = String(p || '').toLowerCase();
  if (v === 'highest') return { style: { backgroundColor: '#dc3545', color: '#fff' } }; // red
  if (v === 'high')    return { style: { backgroundColor: '#fd7e14', color: '#fff' } }; // orange
  if (v === 'medium')  return { style: { backgroundColor: '#ffc107', color: '#212529' } }; // yellow
  if (v === 'low' || v === 'lowest') return { style: { backgroundColor: '#0d6efd', color: '#fff' } }; // blue
  return { style: { backgroundColor: '#6c757d', color: '#fff' } };
}

// Order matters — drives the dropdown order. Mirrors backend TASK_STATUS.
const STATUS_OPTIONS = ['NEW', 'IN_PROGRESS', 'DONE'];

export default function TaskDetailModal({ taskId, show, onClose, currentUserId }) {
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [practiceUsers, setPracticeUsers] = useState([]);
  // Lightbox: when non-null, renders a fullscreen viewer over the modal.
  const [lightboxUrl, setLightboxUrl] = useState(null);
  // Inline image-attachment uploader (parity with CreateTaskModal). The task
  // already exists at this point, so files upload on selection — no submit
  // step. Disabled once existing + in-flight count hits MAX_ATTACHMENTS.
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const navigate = useNavigate();
  const recording = useRecordingActions();

  // Two flavours of fetch:
  //   - First open: setLoading(true) so the spinner shows while we wait.
  //   - Silent refresh: triggered after a status/assignee/comment change.
  //     Keep the existing task body visible so the modal doesn't flash a
  //     "Loading task…" spinner mid-edit. On error, surface the error
  //     without nuking the existing task data.
  const fetchTask = useCallback(async (id, { silent = false } = {}) => {
    if (!id) return;
    if (!silent) setLoading(true);
    setError(null);
    const data = await getTask(id);
    if (!data) {
      setError('Could not load this task.');
      if (!silent) {
        setTask(null);
        setComments([]);
      }
    } else {
      setTask(data.task);
      setComments(Array.isArray(data.comments) ? data.comments : []);
    }
    if (!silent) setLoading(false);
  }, []);

  // Initial load + practice users.
  useEffect(() => {
    if (!show || !taskId) return;
    fetchTask(taskId).catch(() => {});
    // markTaskRead is fired server-side as a side-effect of GET /:taskId, but
    // we still post it explicitly so the badge clears immediately even if the
    // user is on a stale list — idempotent.
    markTaskRead(taskId).catch(() => {});
  }, [show, taskId, fetchTask]);

  useEffect(() => {
    if (!show) return;
    listPracticeUsers().then((users) => setPracticeUsers(Array.isArray(users) ? users : []));
  }, [show]);

  const handleAddComment = async ({ commentText, mentionedUserIds }) => {
    if (!task) return;
    setSubmitting(true);
    try {
      await addTaskComment(task._id, { commentText, mentionedUserIds });
      await fetchTask(task._id, { silent: true });
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Could not post comment.');
    } finally {
      setSubmitting(false);
    }
  };
  const handleAddReply = async ({ parentCommentId, commentText, mentionedUserIds }) => {
    if (!task) return;
    setSubmitting(true);
    try {
      await addTaskComment(task._id, { parentCommentId, commentText, mentionedUserIds });
      await fetchTask(task._id, { silent: true });
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Could not post reply.');
    } finally {
      setSubmitting(false);
    }
  };

  // Generic status change — wired to the click-to-change status dropdown.
  // No-op if the user picks the current status.
  const handleStatusChange = async (nextStatus) => {
    if (!task || nextStatus === task.status) return;
    setSubmitting(true);
    try {
      await patchTaskStatus(task._id, nextStatus);
      await fetchTask(task._id, { silent: true });
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Could not update status.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssigneeChange = async (newAssigneeId) => {
    if (!task) return;
    if (String(newAssigneeId || '') === String(task.assignedToUserId || '')) return;
    setSubmitting(true);
    try {
      await patchTaskAssignee(task._id, newAssigneeId || null);
      await fetchTask(task._id, { silent: true });
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Could not change assignee.');
    } finally {
      setSubmitting(false);
    }
  };

  // Approval-task actions. The backend re-validates SuperAdmin + practice
  // scope so a stale UI can't bypass them; client gating is UX only.
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approvalError, setApprovalError] = useState(null);

  const handleApprove = async () => {
    if (!task) return;
    setSubmitting(true);
    setApprovalError(null);
    try {
      await approveTask(task._id);
      await fetchTask(task._id, { silent: true });
    } catch (e) {
      const code = e?.response?.data?.error;
      if (code === 'already_actioned') {
        setApprovalError('Another SuperAdmin has already actioned this request.');
      } else if (code === 'era_sourced_receipt') {
        setApprovalError('Receipt is ERA-sourced and cannot be reversed here.');
      } else if (code === 'drawn_down') {
        const u = e?.response?.data?.details?.usedAmount;
        setApprovalError(`R ${Number(u || 0).toFixed(2)} of unallocated funds already used downstream — reverse that payment first.`);
      } else if (code === 'already_reversed') {
        setApprovalError('Receipt has already been reversed.');
      } else {
        setApprovalError(code || e?.message || 'Approval failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };
  const handleReject = async () => {
    if (!task) return;
    if (!rejectReason.trim()) {
      setApprovalError('A rejection reason is required.');
      return;
    }
    setSubmitting(true);
    setApprovalError(null);
    try {
      await rejectTask(task._id, { reason: rejectReason.trim() });
      setRejectMode(false);
      setRejectReason('');
      await fetchTask(task._id, { silent: true });
    } catch (e) {
      const code = e?.response?.data?.error;
      if (code === 'already_actioned') {
        setApprovalError('Another SuperAdmin has already actioned this request.');
      } else {
        setApprovalError(code || e?.message || 'Rejection failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Add-image flow on the detail view. Validates MIME, enforces the
  // MAX_ATTACHMENTS cap (existing + new), uploads in one multipart request,
  // then silent-refreshes the task so the new thumbnails appear.
  const handleFileSelect = async (e) => {
    const incoming = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same file later
    if (!incoming.length || !task) return;

    const accepted = [];
    const rejectedMsgs = [];
    for (const f of incoming) {
      if (!ALLOWED_MIME.includes(f.type)) {
        rejectedMsgs.push(`${f.name}: unsupported format`);
        continue;
      }
      accepted.push(f);
    }

    const existingCount = Array.isArray(task.attachments) ? task.attachments.length : 0;
    const remaining = Math.max(0, MAX_ATTACHMENTS - existingCount);
    const trimmed = accepted.slice(0, remaining);
    const overflow = accepted.length - trimmed.length;
    const overflowMsg = overflow > 0 ? `${overflow} image(s) skipped — max ${MAX_ATTACHMENTS} per task.` : '';

    if (!trimmed.length) {
      setUploadError([...rejectedMsgs, overflowMsg].filter(Boolean).join(' ') || 'No supported images selected.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      await uploadTaskAttachments(task._id, trimmed);
      await fetchTask(task._id, { silent: true });
      const warn = [...rejectedMsgs, overflowMsg].filter(Boolean).join(' ');
      if (warn) setUploadError(warn);
    } catch (err) {
      setUploadError(err?.response?.data?.error || err?.message || 'Attachment upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleOpenPatient = () => {
    if (!task?.patientId) return;
    const apptId = task.appointmentId || '1';
    navigate(`/patients/appointment/${task.patientId}/${apptId}`);
    // For TRANSCRIPTION_REVIEW tasks, also open the SyncTo ReviewModal so
    // the user lands directly in the review flow — same UX as clicking the
    // bell notification for a syncto:job-complete event.
    if (
      task.taskType === 'TRANSCRIPTION_REVIEW'
      && task.transcriptionJobId
      && recording
      && typeof recording.openJobForReview === 'function'
    ) {
      recording.openJobForReview(task.transcriptionJobId, { force: true });
    }
    onClose?.();
  };

  // Temporarily hidden — the intake-review UI needs refinement before we
  // surface navigation to it from tasks. Flip to true to re-enable the
  // "Open Intake Review" button below.
  const SHOW_INTAKE_REVIEW_LINK = false;

  const handleOpenIntakeReview = () => {
    if (!task?.intakeReviewId) return;
    navigate(`/intake-reviews?reviewId=${task.intakeReviewId}`);
    onClose?.();
  };

  const handleOpenReleaseNotes = () => {
    navigate('/userprofile?tab=releasenotes');
    onClose?.();
  };

  // Lands on the patient's Documents panel with the saved draft open for
  // review. The section and draft id travel in router state, which
  // patientsappointment.jsx reads on arrival.
  const handleOpenDocument = () => {
    if (!task?.patientId || !task?.documentId) return;
    const apptId = task.appointmentId || '1';
    navigate(`/patients/appointment/${task.patientId}/${apptId}`, {
      state: { section: 'documents', documentId: task.documentId },
    });
    onClose?.();
  };

  const isAi = task?.taskType === 'AI_FOLLOW_UP';
  const isDocument = task?.taskType === 'DOCUMENT_READY';
  const isSupport = task?.taskType === 'SUPPORT';
  const isApproval = task?.taskType === 'APPROVAL';
  const isIntakeReview = task?.taskType === 'INTAKE_REVIEW';
  const isReleaseNotes = task?.taskType === 'RELEASE_NOTES';
  const isPractoSupportAssignee = task?.assignedToUserId === 'PRACTO_SUPPORT';

  // SuperAdmin gate for the Approve / Reject buttons. Server re-checks
  // independently — this is purely UX so non-SAs don't see actions they
  // can't perform.
  const viewerIsSuperAdmin = useMemo(() => {
    const t = sessionStorage.getItem('userToken') || sessionStorage.getItem('user');
    if (!t) return false;
    try {
      const claims = jwtDecode(t);
      const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
      const roles = new Set([
        ...asArray(claims.role),
        ...asArray(claims.roles),
        ...asArray(claims.permissions),
      ]);
      return roles.has('SuperAdmin');
    } catch {
      return false;
    }
  }, []);

  const approvalPayload = isApproval ? (task.approvalPayload || {}) : null;
  const approvalStatus = isApproval ? (task.approvalStatus || 'PENDING') : null;
  const approvalIsPending = approvalStatus === 'PENDING';
  const approvalStatusVariant =
    approvalStatus === 'APPROVED' ? 'success' :
    approvalStatus === 'REJECTED' ? 'danger' :
    'warning';
  const approvalStatusLabel =
    approvalStatus === 'APPROVED' ? 'Approved' :
    approvalStatus === 'REJECTED' ? 'Rejected' :
    'Pending approval';
  const created = useMemo(() => {
    if (!task?.createdAt) return '';
    try { return new Date(task.createdAt).toLocaleString('en-GB'); } catch { return ''; }
  }, [task]);

  return (
    <Modal show={show} onHide={onClose} size="lg" centered scrollable>
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Task details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4 text-muted">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading task…
          </div>
        )}
        {!loading && error && (
          <Alert variant="danger" className="small mb-3">{error}</Alert>
        )}
        {!loading && task && (
          <>
            <div className="mb-3">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                {isSupport ? (
                  // SUPPORT tasks are JIRA-driven — render the JIRA status as
                  // a read-only badge. Manual status / priority edits would be
                  // overwritten by the next webhook anyway.
                  <>
                    <Badge bg={statusVariant(task.status)} pill title="JIRA status">
                      {task.jiraStatus || statusLabel(task.status)}
                    </Badge>
                    {task.jiraIssueKey && (
                      <Badge bg="light" text="dark" pill className="border" title="JIRA issue key">
                        {task.jiraIssueKey}
                      </Badge>
                    )}
                    {task.priority && (
                      <Badge bg="" pill title="Priority" {...priorityBadgeProps(task.priority)}>
                        {task.priority}
                      </Badge>
                    )}
                  </>
                ) : isApproval ? (
                  // APPROVAL tasks have their own lifecycle (PENDING /
                  // APPROVED / REJECTED) — surface that as a read-only
                  // badge so manual status edits can't desync the group.
                  <Badge bg={approvalStatusVariant} pill title="Approval status">
                    {approvalStatusLabel}
                  </Badge>
                ) : (
                  /* Click-to-change status. Toggle is styled to match a
                     Bootstrap Badge so it visually reads the same as before
                     — but it's a button: clicking opens a small dropdown of
                     the three valid statuses. The chosen status patches
                     immediately and the task refetches. */
                  <Dropdown>
                    <Dropdown.Toggle
                      as="button"
                      type="button"
                      disabled={submitting}
                      className={`badge bg-${statusVariant(task.status)} rounded-pill border-0 d-inline-flex align-items-center gap-1`}
                      style={{ cursor: 'pointer', fontSize: '0.75em' }}
                      title="Change status"
                    >
                      {statusLabel(task.status)}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {STATUS_OPTIONS.map((s) => (
                        <Dropdown.Item
                          key={s}
                          active={s === task.status}
                          onClick={() => handleStatusChange(s)}
                        >
                          <Badge bg={statusVariant(s)} pill className="me-2">
                            {statusLabel(s)}
                          </Badge>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                )}
                {isAi && (
                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement="bottom"
                    overlay={
                      <Popover id={`ai-popover-${task._id}`}>
                        <Popover.Header className="small">
                          <i className="fa-light fa-sparkles me-1" />AI-suggested task
                        </Popover.Header>
                        <Popover.Body className="small">
                          <div className="mb-2">
                            This task was suggested by SyncTo based on the transcription.
                            Please review before actioning.
                          </div>
                          {task.aiMeta?.reason && (
                            <div className="mb-1"><strong>Reason:</strong> {task.aiMeta.reason}</div>
                          )}
                          {task.aiMeta?.sourceExcerpt && (
                            <div><strong>Excerpt:</strong> <em>"{task.aiMeta.sourceExcerpt}"</em></div>
                          )}
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    <Badge
                      bg="warning"
                      text="dark"
                      pill
                      style={{ cursor: 'help' }}
                      tabIndex={0}
                    >
                      <i className="fa-light fa-sparkles me-1" />
                      AI-suggested
                    </Badge>
                  </OverlayTrigger>
                )}
                <Badge bg="light" text="dark" pill>{task.source || 'SyncTo'}</Badge>
              </div>
              <div className="fw-semibold mb-1" style={{ wordBreak: 'break-word' }}>
                {task.description}
              </div>
              <div className="small text-muted">
                Created {created}
                {task.createdByName && <span> by {task.createdByName}</span>}
                {task.patientName && <span> · Patient: {task.patientName}</span>}
              </div>
              {isReleaseNotes && (
                <div className="small text-muted mt-1">
                  <i className="fa-light fa-circle-info me-1" />
                  This task can only be marked done after you have viewed the
                  release notes.
                </div>
              )}
            </div>

            {isApproval && (
              <div className="border rounded p-3 mb-3 bg-light">
                <div className="fw-semibold mb-2">
                  <i className="fa-light fa-circle-info me-2 text-primary" />
                  Approval request
                </div>
                {approvalPayload?.accountName && (
                  <div className="small mb-1">
                    <span className="text-muted">Account:</span>{' '}
                    <strong>{approvalPayload.accountName}</strong>
                  </div>
                )}
                {approvalPayload?.invoiceNumber && (
                  <div className="small mb-1">
                    <span className="text-muted">Invoice:</span>{' '}
                    <strong>{approvalPayload.invoiceNumber}</strong>
                    {approvalPayload.totalAmount != null && (
                      <span className="ms-2">
                        R {Number(approvalPayload.totalAmount).toFixed(2)}
                      </span>
                    )}
                  </div>
                )}
                {approvalPayload?.receiptNumber && (
                  <div className="small mb-1">
                    <span className="text-muted">Receipt:</span>{' '}
                    <strong>{approvalPayload.receiptNumber}</strong>
                    {approvalPayload.receiptTotalAmount != null && (
                      <span className="ms-2">
                        R {Number(approvalPayload.receiptTotalAmount).toFixed(2)}
                      </span>
                    )}
                  </div>
                )}
                {Array.isArray(approvalPayload?.affectedInvoiceNumbers) &&
                  approvalPayload.affectedInvoiceNumbers.length > 0 && (
                    <div className="small mb-1">
                      <span className="text-muted">Invoices affected:</span>{' '}
                      {approvalPayload.affectedInvoiceNumbers.join(', ')}
                    </div>
                  )}
                {approvalPayload?.unallocatedAmount > 0 && (
                  <div className="small mb-1">
                    <span className="text-muted">Unallocated to remove:</span>{' '}
                    R {Number(approvalPayload.unallocatedAmount).toFixed(2)}
                  </div>
                )}
                {(approvalPayload?.requesterName || task.createdByName) && (
                  <div className="small mb-1">
                    <span className="text-muted">Requested by:</span>{' '}
                    {approvalPayload?.requesterName || task.createdByName}
                  </div>
                )}
                {approvalPayload?.reason && (
                  <div className="small mb-2">
                    <span className="text-muted">Reason:</span>{' '}
                    <em>"{approvalPayload.reason}"</em>
                  </div>
                )}

                {!approvalIsPending && (
                  <Alert
                    variant={approvalStatusVariant === 'success' ? 'success' : 'danger'}
                    className="small mb-0 mt-2"
                  >
                    {approvalStatusLabel}
                    {task.approvalRespondedByName && (
                      <> by <strong>{task.approvalRespondedByName}</strong></>
                    )}
                    {task.approvalRespondedAt && (
                      <> on {new Date(task.approvalRespondedAt).toLocaleString('en-GB')}</>
                    )}
                    {task.approvalResponseReason && (
                      <div className="mt-1">
                        <em>"{task.approvalResponseReason}"</em>
                      </div>
                    )}
                  </Alert>
                )}

                {approvalIsPending && viewerIsSuperAdmin && (
                  <div className="mt-3">
                    {approvalError && (
                      <Alert variant="danger" className="small mb-2">
                        {approvalError}
                      </Alert>
                    )}
                    {!rejectMode ? (
                      <div className="d-flex gap-2">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={handleApprove}
                          disabled={submitting}
                        >
                          {submitting ? <Spinner size="sm" animation="border" /> : 'Approve'}
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => { setRejectMode(true); setApprovalError(null); }}
                          disabled={submitting}
                        >
                          Reject
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Form.Group className="mb-2">
                          <Form.Label className="small mb-1">
                            Rejection reason <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Why is this being rejected?"
                          />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => { setRejectMode(false); setApprovalError(null); setRejectReason(''); }}
                            disabled={submitting}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={handleReject}
                            disabled={submitting || !rejectReason.trim()}
                          >
                            {submitting ? <Spinner size="sm" animation="border" /> : 'Confirm reject'}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {approvalIsPending && !viewerIsSuperAdmin && (
                  <div className="small text-muted mt-2">
                    Awaiting SuperAdmin response.
                  </div>
                )}
              </div>
            )}

            <Form.Group className="mb-3" controlId={`task-assignee-${task._id}`}>
              <Form.Label className="small text-muted mb-1">Assigned to</Form.Label>
              {isSupport || isReleaseNotes ? (
                // Assignee on SUPPORT tasks is system-managed: PRACTO_SUPPORT
                // until you reply (which reassigns to you), then back to
                // PRACTO_SUPPORT when JIRA replies again. RELEASE_NOTES tasks
                // are personal (one per user) and can't be reassigned either.
                // Read-only chip for both.
                <div className="small">
                  <i className={`fa-light ${isPractoSupportAssignee ? 'fa-life-ring' : 'fa-user'} me-1`} />
                  {task.assignedToName || 'Unassigned'}
                </div>
              ) : (
                <Form.Select
                  size="sm"
                  value={task.assignedToUserId || ''}
                  onChange={(e) => handleAssigneeChange(e.target.value)}
                  disabled={submitting}
                >
                  <option value="">Unassigned</option>
                  {practiceUsers.map((u) => {
                    const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'User';
                    return <option key={u._id} value={u._id}>{name}</option>;
                  })}
                </Form.Select>
              )}
            </Form.Group>

            <div className="d-flex flex-wrap gap-2 mb-3">
              {isDocument && task.documentId && task.patientId && (
                <Button variant="primary" size="sm" onClick={handleOpenDocument}>
                  <i className="fa-light fa-file-lines me-1" />Open Document
                </Button>
              )}
              {task.patientId && (
                <Button variant="outline-primary" size="sm" onClick={handleOpenPatient}>
                  <i className="fa-light fa-user-injured me-1" />Open Patient
                </Button>
              )}
              {SHOW_INTAKE_REVIEW_LINK && isIntakeReview && task.intakeReviewId && (
                <Button variant="outline-primary" size="sm" onClick={handleOpenIntakeReview}>
                  <i className="fa-light fa-file-signature me-1" />Open Intake Review
                </Button>
              )}
              {isReleaseNotes && (
                <Button variant="outline-primary" size="sm" onClick={handleOpenReleaseNotes}>
                  <i className="fa-light fa-bullhorn me-1" />View Release Notes
                </Button>
              )}
            </div>

            {!isReleaseNotes && (() => {
              const existingAtts = Array.isArray(task.attachments) ? task.attachments : [];
              const remaining = Math.max(0, MAX_ATTACHMENTS - existingAtts.length);
              const addDisabled = uploading || submitting || remaining === 0;
              return (
                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="small text-muted">
                      Attachments
                      <span className="ms-2">{existingAtts.length}/{MAX_ATTACHMENTS}</span>
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ALLOWED_MIME.join(',')}
                      capture="environment"
                      multiple
                      className="d-none"
                      onChange={handleFileSelect}
                      disabled={addDisabled}
                    />
                    <Button
                      type="button"
                      variant="outline-primary"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={addDisabled}
                    >
                      {uploading ? (
                        <><Spinner animation="border" size="sm" className="me-2" />Uploading…</>
                      ) : (
                        <><i className="fa-light fa-camera me-2" />Add image / take photo</>
                      )}
                    </Button>
                  </div>

                  {uploadError && (
                    <Alert variant="warning" className="py-2 small mb-2">
                      {uploadError}
                    </Alert>
                  )}

                  {existingAtts.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {existingAtts.map((att) => (
                        att.url ? (
                          <button
                            key={att.id || att.blobName}
                            type="button"
                            className="p-0 border-0 bg-transparent"
                            onClick={() => setLightboxUrl(att.url)}
                            title={att.originalName || 'Open image'}
                            style={{ width: 96, height: 96, cursor: 'zoom-in' }}
                          >
                            <img
                              src={att.url}
                              alt={att.originalName || 'Task attachment'}
                              className="rounded border"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </button>
                        ) : (
                          <div
                            key={att.id || att.blobName}
                            className="rounded border d-flex flex-column align-items-center justify-content-center text-muted small"
                            style={{ width: 96, height: 96, background: '#f4f4f4' }}
                            title={`${att.originalName || 'Image'} — URL unavailable`}
                          >
                            <i className="fa-light fa-image" style={{ fontSize: '1.6rem' }} />
                            <span style={{ fontSize: '0.65rem' }}>No URL</span>
                          </div>
                        )
                      ))}
                    </div>
                  ) : (
                    <Form.Text muted className="d-block">
                      No attachments yet. JPEG, PNG, or WebP — compressed automatically.
                    </Form.Text>
                  )}
                </div>
              );
            })()}

            {/* RELEASE_NOTES tasks are read-only announcements — the backend
                rejects comments on them, so don't render the thread at all. */}
            {!isReleaseNotes && (
              <>
                <hr className="my-3" />

                <h6 className="mb-3">Comments</h6>
                <TaskCommentList
                  comments={comments}
                  practiceUsers={practiceUsers}
                  onAddComment={handleAddComment}
                  onAddReply={handleAddReply}
                  submitting={submitting}
                  disableReplies={isSupport}
                />
              </>
            )}
          </>
        )}
      </Modal.Body>
      {/* Lightbox — fixed-position overlay rather than a nested <Modal>.
          A nested react-bootstrap Modal stacks BEHIND the parent's backdrop
          due to z-index assignment order, which is why clicking a
          thumbnail previously appeared to do nothing. Rendered as a portal-
          like fixed overlay with z-index 2000 (above Bootstrap's modal
          z-index of ~1055). Click the backdrop OR the image to dismiss. */}
      {lightboxUrl && (
        <div
          role="dialog"
          aria-label="Attachment preview"
          onClick={() => setLightboxUrl(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            cursor: 'zoom-out',
          }}
        >
          <img
            src={lightboxUrl}
            alt="Attachment"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setLightboxUrl(null);
            }}
          />
        </div>
      )}
    </Modal>
  );
}
